import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createGetDataThunk, createInitialProcDataState } from '../../common/procSliceCommons'
import { dataDictionary } from '../../data/dd'
import { PefusateLevelFields, TEntryField, TMemProcData, TStepData } from '../../data/ProcData'
import { getPreviousAssessmentStep, isAssessmentStep, isRecruitmentStep, TTimelineStep } from '../../data/WorkflowSteps'
import { RunState } from './RunStore'

export const getProcDataAsync = createGetDataThunk('procTimeline/fetchProcData')
const initialState = createInitialProcDataState()

export interface ISetFieldPayload {
  step: TTimelineStep
  fieldName: TEntryField
  value: number
}

export interface ISetFlagPayload {
  step: TTimelineStep
  fieldName: TEntryField
  value: boolean
}

export interface IRecalculateStaticCompliancePayload {
  step: TTimelineStep
  assessmentVV_mL?: number
  PEEP_cmH20?: number
}

type TCalculatedField =
  | 'PVResistance'
  | 'PerfusateLoss'
  | 'DeltaPF'
  | 'DeltaPFRUV'
  | 'DeltaPFRLV'
  | 'DeltaPFLUV'
  | 'DeltaPFLLV'
  | 'StaticCompliance'

type TPulmonaryVeinPO2Field =
  | 'RUVPO2'
  | 'RLVPO2'
  | 'LUVPO2'
  | 'LLVPO2'

type TPulmonaryVeinDeltaField =
  | 'DeltaPFRUV'
  | 'DeltaPFRLV'
  | 'DeltaPFLUV'
  | 'DeltaPFLLV'

const pulmonaryVeinDeltaFields: Record<TPulmonaryVeinPO2Field, TPulmonaryVeinDeltaField> = {
  RUVPO2: 'DeltaPFRUV',
  RLVPO2: 'DeltaPFRLV',
  LUVPO2: 'DeltaPFLUV',
  LLVPO2: 'DeltaPFLLV'
}

// to Numeric Perfusate Field
function toNPF(value: string | number | boolean | undefined) {
  return (value ?? 0) as number
}

function getDecimalPlaces(value: number) {
  const text = value.toString()
  const index = text.indexOf('.')

  if (index === -1) {
    return 0
  }

  return text.length - index - 1
}

function roundCalculatedValue(field: TCalculatedField, value: number) {
  const step = dataDictionary[field].step
  const decimalPlaces = getDecimalPlaces(step)
  const scale = Math.pow(10, decimalPlaces)
  const scaledStep = Math.round(step * scale)

  const roundedValue =
    Math.round((value * scale) / scaledStep) * scaledStep / scale

  return Object.is(roundedValue, -0) ? 0 : roundedValue
}

export function PerfusateLostCalculator(data: TMemProcData, step: TTimelineStep, field: TEntryField) {
  let retval = undefined

  if (step !== '0:00'
    && isAssessmentStep(step)
    && (PefusateLevelFields as any as string[]).includes(field)) {
    if (step === '0:55') {
      retval = toNPF(data['0:00'].PerfusateLevelInitial)
        + toNPF(data['0:55'].PerfusateAddedPre)
        - toNPF(data['0:55'].PerfusateLevelPre)
    } else {
      const prevStep = getPreviousAssessmentStep(step)
      const assessmentLost = toNPF(data[prevStep].PerfusateLevelPre)
        - toNPF(data[prevStep].PerfusateLevelPost)
      const hourLost = toNPF(data[prevStep].PerfusateLevelPostExchange)
        + toNPF(data[step].PerfusateAddedPre)
        - toNPF(data[step].PerfusateLevelPre)
      retval = assessmentLost + hourLost
    }
  }

  return retval === undefined
    ? undefined
    : roundCalculatedValue('PerfusateLoss', retval)
}

export function DeltaPFCalculator(stepData: TStepData, field: TEntryField) {
  let retval = undefined

  if (field === 'LAPO2' || field === 'PAPO2') {
    if (stepData.LAPO2 !== undefined
      && stepData.PAPO2 !== undefined) {
      retval = (stepData.LAPO2 as number) - (stepData.PAPO2 as number)
    }
  }

  return retval === undefined
    ? undefined
    : roundCalculatedValue('DeltaPF', retval)
}

export function PVResistanceCalculator(stepData: TStepData, field: TEntryField) {
  let retval = undefined

  if (field === 'PAPressure' || field === 'LAPressure' || field === 'PerfusateFlow') {
    if (stepData.PAPressure !== undefined
      && stepData.LAPressure !== undefined
      && stepData.PerfusateFlow !== undefined) {
      retval = (((stepData.PAPressure as number) - (stepData.LAPressure as number)) * 80)
        / (stepData.PerfusateFlow as number / 1000)
    }
  }

  return retval === undefined
    ? undefined
    : roundCalculatedValue('PVResistance', retval)
}

export function StaticComplianceCalculator(
  stepData: TStepData,
  assessmentVV_mL?: number,
  PEEP_cmH20?: number
) {
  let retval = undefined

  if (stepData.PlateauAWP !== undefined
    && assessmentVV_mL !== undefined
    && PEEP_cmH20 !== undefined) {
    const plateauAWP = stepData.PlateauAWP as number
    const denominator = plateauAWP - PEEP_cmH20

    if (Number.isFinite(plateauAWP)
      && Number.isFinite(assessmentVV_mL)
      && Number.isFinite(PEEP_cmH20)
      && denominator > 0) {
      retval = assessmentVV_mL / denominator
    }
  }

  return retval === undefined
    ? undefined
    : roundCalculatedValue('StaticCompliance', retval)
}

export function DeltaPFPulmonaryVeinCalculator(
  dependedField: TPulmonaryVeinPO2Field,
  stepData: TStepData,
  field: TEntryField
) {
  let retval = undefined

  if (field === dependedField || field === 'PAPO2') {
    if (stepData[dependedField] !== undefined
      && stepData.PAPO2 !== undefined) {
      retval = (stepData[dependedField] as number) - (stepData.PAPO2 as number)
    }
  }

  return retval === undefined
    ? undefined
    : roundCalculatedValue(pulmonaryVeinDeltaFields[dependedField], retval)
}

export const timelineSlice = createSlice({
  name: 'timeline',
  initialState: initialState,
  reducers: {
    setTimelineData: (state, action: PayloadAction<TMemProcData>) => {
      state.timelineData = action.payload
    },
    setfield: (state, action: PayloadAction<ISetFieldPayload>) => {
      state.timelineData[action.payload.step][action.payload.fieldName] = action.payload.value

      state.timelineData[action.payload.step].PVResistance =
        PVResistanceCalculator(state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].PVResistance

      state.timelineData[action.payload.step].PerfusateLoss = PerfusateLostCalculator(
        state.timelineData,
        action.payload.step,
        action.payload.fieldName
      )
        ?? state.timelineData[action.payload.step].PerfusateLoss

      state.timelineData[action.payload.step].DeltaPF =
        DeltaPFCalculator(state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].DeltaPF

      state.timelineData[action.payload.step].DeltaPFRUV =
        DeltaPFPulmonaryVeinCalculator('RUVPO2', state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].DeltaPFRUV

      state.timelineData[action.payload.step].DeltaPFRLV =
        DeltaPFPulmonaryVeinCalculator('RLVPO2', state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].DeltaPFRLV

      state.timelineData[action.payload.step].DeltaPFLUV =
        DeltaPFPulmonaryVeinCalculator('LUVPO2', state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].DeltaPFLUV

      state.timelineData[action.payload.step].DeltaPFLLV =
        DeltaPFPulmonaryVeinCalculator('LLVPO2', state.timelineData[action.payload.step], action.payload.fieldName)
        ?? state.timelineData[action.payload.step].DeltaPFLLV
    },
    recalculateStaticCompliance: (state, action: PayloadAction<IRecalculateStaticCompliancePayload>) => {
      state.timelineData[action.payload.step].StaticCompliance =
        StaticComplianceCalculator(
          state.timelineData[action.payload.step],
          action.payload.assessmentVV_mL,
          action.payload.PEEP_cmH20
        )
    },
    setflag: (state, action: PayloadAction<ISetFlagPayload>) => {
      state.timelineData[action.payload.step][action.payload.fieldName] = action.payload.value

      if (isRecruitmentStep(action.payload.step)) {
        if (state.timelineData[action.payload.step]['IsTidalVolumeIncreased']
          && state.timelineData[action.payload.step]['IsHoldsPerformed']
          && state.timelineData[action.payload.step]['IsTidalVolumeRestored']) {
          state.timelineData[action.payload.step].IsMandatoryDataEntered = true
        } else {
          state.timelineData[action.payload.step].IsMandatoryDataEntered = false
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProcDataAsync.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getProcDataAsync.fulfilled, (state, action) => {
        state.status = 'idle'

        if (action.payload) {
          state.timelineData = action.payload
        }
      })
      .addCase(getProcDataAsync.rejected, (state) => {
        state.status = 'failed'
      })
  }
})

export const selectData = (state: RunState) => state.timelineSlice.timelineData
export const { setTimelineData, setfield, setflag, recalculateStaticCompliance } = timelineSlice.actions

export default timelineSlice.reducer